import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy, OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ILoader } from "libraries/component/loader/ILoader";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import {
    ISimpleSelectFieldConfig,
    ISimpleSelectOption
} from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { IGetPropertyResponse } from "libraries/service/resources/hotel/ISearchResponse";
import { IHotelProperty } from "libraries/service/resources/model/search/hotel/IHotelProperty";
import { Observable, of, Subscription } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { HotelInformationsModalService } from "../service/hotel-informations-modal.service";
import { IAlternative } from "libraries/hotel/models/hotel";
import { giata } from "libraries/hotel/containers/hotels-list/config";
import { IProperty } from "libraries/hotel/models/quote";
import { PropertyResourceService } from "libraries/service/resources/hotel/property-resource.service";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";

enum FormControlName {
    hostPropertySyscode = "hostPropertySyscode"
}

interface IFormValue {
    [FormControlName.hostPropertySyscode]: string;
}

@Component({
    selector: 'hotel-informations-alternative-select',
    template: `
        <loader *ngIf="loader.active" [message]="loader.message" [type]="loader.type"></loader>
        <notification *ngIf="error.active" [type]="error.type">
            {{ error.message }}
        </notification>
        <form permissionShow="shared.host.show" *ngIf="!loader.active && formGroup && alternativeFieldConfig && isPermModalAlternative && propertyAlternativesCache?.length"
              [formGroup]="formGroup"
              [hidden]="!hotelHostSelected">
            <simple-select [class.width-and-white-background]="widthAndWhiteBackground"
                           [withBorder]="true"
                           [fieldConfig]="alternativeFieldConfig"
                           [isEditable]="isActive">
            </simple-select>
        </form>
    `,
    styleUrls: ['./hotel-informations-alternative-select.component.scss'],
})
export class HotelInformationsAlternativeSelectComponent implements OnInit, OnChanges, OnDestroy {
    /** Required. */
    @Input()
    public currentProperty: IProperty;
    @Input()
    public propertyAlternatives: IAlternative[];
    public propertyAlternativesCache: IAlternative[];

    @Input()
    public isHostOrGiataChoice: boolean = false;
    @Input()
    public initWithGiata: boolean = false;
    @Input()
    public isWithGiata: boolean = false;
    @Input()
    public widthAndWhiteBackground: boolean = false;
    @Input()
    public isPermModalAlternative: boolean;
    @Input()
    public isActive: boolean = false;

    @Output()
    public readonly currentPropertyChange: EventEmitter<IHotelProperty> = new EventEmitter();

    public formGroup: FormGroup = new FormGroup({
        [FormControlName.hostPropertySyscode]: new FormControl(''),
    });
    public alternativeFieldConfig: ISimpleSelectFieldConfig;

    public hotelHostSelected: string;

    public error: INotification = {
        type: NotificationType.Error,
        active: false,
        message: '',
    };

    public readonly loader: ILoader = {
        active: false,
        message: Yii.t('wui-shared-js', "Changement du host sélectionné en cours"),
        type: 'inline',
    };

    public readonly wording: ILabels = {
        changeHostError: Yii.t('wui-iqbh-js', 'Erreur lors du changement d\'host'),
    };

    private readonly subscriptions: Subscription = new Subscription();

    constructor(
        private readonly hotelInformationsModalService: HotelInformationsModalService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly propertyResourceService: PropertyResourceService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.currentProperty?.currentValue !== changes.currentProperty?.previousValue &&
            this.currentProperty.hostCode !== this.hotelHostSelected
        ) {
            if (!this.currentProperty || !this.propertyAlternatives) {
                return;
            }
            const initialHost: string = this.currentProperty.hostCode;
            this.initFields(initialHost);

            //dont change on first ngOnChange, it is done in AfterviewInit. This way, we have the event emmitted.
            //on following changes, value can be changed, but no event emitted, to avoid infinite loop
            if (this.hasAlternativeProperty() && !changes.currentProperty.firstChange && this.isActive) {
                this.initValue(this.hotelHostSelected, false);
            }
        }
        if (this.isActive && !this.hotelHostSelected && !changes.isActive?.firstChange) {
            this.initFirstValue();
        }
    }

    public ngOnInit(): void {
        const formValueChange: Subscription = this.formGroup && this.formGroup.valueChanges.pipe(
            tap((): void => {
                this.loader.active = true;
                this.error.active = false;
            }),
            map((formValue: IFormValue): IAlternative =>
                this.findAlternativeBySyscode(formValue.hostPropertySyscode)),
            switchMap((curAlternative: IAlternative) => {
                if (curAlternative?.hostCode && curAlternative?.syscode) {
                    return this.propertyResourceService.getHostProperty(curAlternative.hostCode, curAlternative.syscode)
                }
                return of(null);
            }),
            catchError((error: Error, caught: Observable<object>) => {
                this.loader.active = false;
                this.error = {
                    ...this.error,
                    active: true,
                    message: error.message || this.wording.changeHostError,
                };
                this.errorTracingService.captureException(error);
                return caught;
            })
        ).subscribe({
            next: (propertyResponse: IGetPropertyResponse): void => {
                this.loader.active = false;
                if (propertyResponse && propertyResponse.property) {
                    this.hotelHostSelected = propertyResponse.property.hostCode;
                    this.currentPropertyChange.emit(propertyResponse.property as unknown as IHotelProperty);
                }
            },
        });

        this.subscriptions.add(formValueChange);

        //init host the first time here
        if (this.isActive) {
            this.initFirstValue();
        }

    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public initFirstValue(): void {
        if (this.propertyAlternativesCache && this.propertyAlternativesCache.length && this.initWithGiata && this.hasHostInInformations(giata)) {
            this.hotelHostSelected = this.currentProperty.hostCode;
            this.initDefaultValue(this.hotelHostSelected);
        } else {
            this.hotelHostSelected = typeof this.currentProperty.host === "string" ?
                this.currentProperty.host as string :
                this.currentProperty.hostCode;
            if (this.hasAlternativeProperty()) {
                this.initValue(this.hotelHostSelected, true);
            }
        }
    }

    public initFields(initialHost: string): void {
        if (!this.hasAlternativeProperty()) {
            this.propertyAlternativesCache = this.propertyAlternatives;
        }
        this.hotelInformationsModalService.getAlternativesOptionsObservable(
            this.propertyAlternativesCache || [],
            this.isWithGiata,
            this.isHostOrGiataChoice,
            initialHost
        ).subscribe({
            next: (alternativeOptions: ISimpleSelectOption[]): void => {
                this.alternativeFieldConfig = {
                    formGroup: this.formGroup,
                    formControlName: FormControlName.hostPropertySyscode,
                    options: alternativeOptions,
                    className: "with-border",
                    placeholder: Yii.t('wui-shared-js', 'Choisir un fournisseur')
                };
                this.changeDetectorRef.detectChanges();
            },
            error: (error: Error): void => {
                this.errorTracingService.captureMessage(error.message);
            }
        });
    }

    public permissions: typeof EStayPermissions = EStayPermissions;

    private hasAlternativeProperty(): boolean {
        return Boolean(this.propertyAlternativesCache) &&
            Boolean(this.propertyAlternativesCache.length);
    }

    private hasHostInInformations(hostCode: string): boolean {
        return this.hasAlternativeProperty() &&
            Boolean(this.propertyAlternativesCache.find((alternative: IAlternative) => alternative.hostCode === hostCode));
    }

    private initDefaultValue(initialHost: string, emitEvent: boolean = true): void {
        // tslint:disable-next-line:no-object-literal-type-assertion
        this.formGroup.setValue({
            [FormControlName.hostPropertySyscode]: (this.initWithGiata && this.hasHostInInformations(giata)) ?
                this.findAlternativeByHostCode(giata).syscode :
                this.findAlternativeByHostCode(initialHost).syscode,
        } as IFormValue, {emitEvent});
    }

    private initValue(host: string, emitEvent: boolean = true): void {
        // tslint:disable-next-line:no-object-literal-type-assertion
        const syscode: string = this.findAlternativeByHostCode(host)?.syscode;
        if (syscode && this.hasAlternativeProperty()) {
            this.formGroup.setValue({
                [FormControlName.hostPropertySyscode]: syscode,
            } as IFormValue, {emitEvent});
        }

    }

    private findAlternativeBySyscode(hostPropertySyscode: string): IAlternative {
        return this.hasAlternativeProperty() ?
            this.propertyAlternativesCache.find((alternative: IAlternative) => alternative.syscode === hostPropertySyscode) :
            null;
    }

    private findAlternativeByHostCode(hostCode: string): IAlternative {
        return this.hasAlternativeProperty() ?
            this.propertyAlternativesCache.find((alternative: IAlternative) => alternative.hostCode === hostCode) :
            null;
    }
}
